import type { ReactElement } from 'react';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PaymentStatus } from '@cookspace-shared-contracts/core';
import { ArrowLeft, RotateCcw } from 'lucide-react';

import { useGetPaymentQuery, useRefundPaymentMutation } from '@/api/paymentsApi';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Textarea } from '@/components/Textarea';
import type { AdminPaymentDetails } from '@/lib/paymentsSchemas';

const STATUS_LABEL: Record<PaymentStatus, string> = {
    pending: 'Ожидает',
    succeeded: 'Оплачен',
    cancelled: 'Отменён',
    failed: 'Ошибка',
    refunded: 'Возврат'
};

const STATUS_COLOR: Record<PaymentStatus, string> = {
    pending: 'bg-yellow-100 text-yellow-800',
    succeeded: 'bg-green-100 text-green-800',
    cancelled: 'bg-gray-100 text-gray-700',
    failed: 'bg-red-100 text-red-800',
    refunded: 'bg-blue-100 text-blue-800'
};

const PRICE_FORMATTER = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
});

const REASON_MIN_LENGTH = 10;

function formatPrice(rub: number): string {
    return PRICE_FORMATTER.format(rub);
}

function formatDate(iso: string | null): string {
    if (iso === null) {
        return '—';
    }
    return new Date(iso).toLocaleString('ru-RU');
}

function formatUserContact(p: AdminPaymentDetails): string {
    const { user } = p;
    if (user === null) {
        return p.userId.slice(0, 8);
    }
    if (user.email !== null && user.email !== '') {
        return user.email;
    }
    if (user.phoneNumber !== null && user.phoneNumber !== '') {
        return user.phoneNumber;
    }
    return user.displayName ?? user.id.slice(0, 8);
}

function isRefundable(p: AdminPaymentDetails): boolean {
    if (p.status !== 'succeeded') {
        return false;
    }
    if (p.refundedAmountRub !== null && p.refundedAmountRub >= p.amountRub) {
        return false;
    }
    return true;
}

export function PaymentDetailPage(): ReactElement {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const { data, isLoading, isError } = useGetPaymentQuery(
        id !== undefined ? { id } : { id: '' },
        { skip: id === undefined || id === '' }
    );
    const [refundPayment, { isLoading: isRefunding }] = useRefundPaymentMutation();

    const [isRefundOpen, setRefundOpen] = useState(false);
    const [refundAmount, setRefundAmount] = useState('');
    const [refundReason, setRefundReason] = useState('');
    const [refundAmountError, setRefundAmountError] = useState<string | null>(null);
    const [refundReasonError, setRefundReasonError] = useState<string | null>(null);
    const [actionError, setActionError] = useState<string | null>(null);

    if (id === undefined || id === '') {
        return (
            <div className="text-sm text-red-600" data-testid="payment-detail-bad-id">
                Не указан ID платежа
            </div>
        );
    }

    if (isLoading) {
        return <div className="text-sm text-gray-500">Загрузка платежа...</div>;
    }

    if (isError || data === undefined) {
        return (
            <div className="text-sm text-red-600" data-testid="payment-detail-error">
                Не удалось загрузить платёж
            </div>
        );
    }

    const payment = data.payment;
    const refundedSoFar = payment.refundedAmountRub ?? 0;
    const maxRefund = payment.amountRub - refundedSoFar;

    const openRefund = (): void => {
        setRefundAmount('');
        setRefundReason('');
        setRefundAmountError(null);
        setRefundReasonError(null);
        setRefundOpen(true);
    };

    const submitRefund = async (): Promise<void> => {
        const reasonTrimmed = refundReason.trim();
        let hasError = false;

        if (reasonTrimmed.length < REASON_MIN_LENGTH) {
            setRefundReasonError(`Минимум ${REASON_MIN_LENGTH} символов`);
            hasError = true;
        } else {
            setRefundReasonError(null);
        }

        let amount: number | undefined;
        if (refundAmount.trim() !== '') {
            const parsed = Number(refundAmount);
            if (!Number.isInteger(parsed) || parsed < 1 || parsed > maxRefund) {
                setRefundAmountError(`Сумма от 1 до ${maxRefund} ₽`);
                hasError = true;
            } else {
                setRefundAmountError(null);
                amount = parsed;
            }
        } else {
            setRefundAmountError(null);
        }

        if (hasError) {
            return;
        }

        try {
            await refundPayment({
                id: payment.id,
                body: {
                    reason: reasonTrimmed,
                    ...(amount !== undefined ? { amount } : {})
                }
            }).unwrap();
            setRefundOpen(false);
        } catch {
            setActionError('Не удалось оформить возврат');
            setRefundOpen(false);
        }
    };

    return (
        <div data-testid="payment-detail-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => navigate('/payments')}
                    className="inline-flex items-center gap-1 text-sm text-gray-600 transition hover:text-gray-900"
                    data-testid="payment-detail-back"
                >
                    <ArrowLeft size={16} />
                    К списку
                </button>
            </div>

            {actionError !== null && actionError !== '' && (
                <div
                    className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    data-testid="payment-detail-action-error"
                >
                    {actionError}
                </div>
            )}

            <div className="rounded border border-gray-200 bg-white p-5">
                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            Платёж {payment.id.slice(0, 8)}
                        </h1>
                        <p className="mt-1 text-sm text-gray-600">
                            Пользователь: {formatUserContact(payment)}
                        </p>
                    </div>
                    <span
                        className={`inline-block rounded px-2 py-1 text-xs font-medium ${STATUS_COLOR[payment.status]}`}
                        data-testid="payment-detail-status"
                    >
                        {STATUS_LABEL[payment.status]}
                    </span>
                </div>

                <dl
                    className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2"
                    data-testid="payment-detail-fields"
                >
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Сумма</dt>
                        <dd className="text-gray-900">{formatPrice(payment.amountRub)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Валюта</dt>
                        <dd className="text-gray-900">{payment.currency}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Провайдер</dt>
                        <dd className="text-gray-900">{payment.provider}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Подписка</dt>
                        <dd className="text-gray-900">
                            {payment.subscriptionId !== null ? (
                                <button
                                    type="button"
                                    onClick={() =>
                                        navigate(`/subscriptions/${payment.subscriptionId}`)
                                    }
                                    className="text-blue-600 underline"
                                    data-testid="payment-detail-subscription-link"
                                >
                                    {payment.subscriptionId.slice(0, 8)}
                                </button>
                            ) : (
                                '—'
                            )}
                        </dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Создан</dt>
                        <dd className="text-gray-900">{formatDate(payment.createdAt)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Оплачен</dt>
                        <dd className="text-gray-900">{formatDate(payment.paidAt)}</dd>
                    </div>
                    <div>
                        <dt className="text-xs font-medium uppercase text-gray-500">Возвращено</dt>
                        <dd className="text-gray-900">
                            {payment.refundedAmountRub !== null
                                ? formatPrice(payment.refundedAmountRub)
                                : '—'}
                        </dd>
                    </div>
                    <div className="sm:col-span-2">
                        <dt className="text-xs font-medium uppercase text-gray-500">Описание</dt>
                        <dd className="text-gray-900">{payment.description ?? '—'}</dd>
                    </div>
                    {payment.confirmationUrl !== null && (
                        <div className="sm:col-span-2">
                            <dt className="text-xs font-medium uppercase text-gray-500">
                                Confirmation URL
                            </dt>
                            <dd className="break-all">
                                <a
                                    href={payment.confirmationUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 underline"
                                    data-testid="payment-detail-confirmation-url"
                                >
                                    {payment.confirmationUrl}
                                </a>
                            </dd>
                        </div>
                    )}
                </dl>
            </div>

            {isRefundable(payment) && (
                <div className="sticky bottom-0 -mx-6 flex items-center justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                    <button
                        type="button"
                        onClick={openRefund}
                        disabled={isRefunding}
                        data-testid="payment-detail-refund-button"
                        className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                    >
                        <RotateCcw size={14} />
                        Оформить возврат
                    </button>
                </div>
            )}

            <Modal
                isOpen={isRefundOpen}
                onClose={() => setRefundOpen(false)}
                title="Оформить возврат"
                testId="payment-detail-refund-modal"
            >
                <div className="space-y-4">
                    <p className="text-sm text-gray-700">
                        Платёж{' '}
                        <span className="font-medium">{formatPrice(payment.amountRub)}</span>.
                        {payment.refundedAmountRub !== null && (
                            <span className="block text-xs text-gray-600">
                                Уже возвращено: {formatPrice(payment.refundedAmountRub)}
                            </span>
                        )}
                    </p>

                    <Input
                        label={`Сумма возврата (₽), пусто = полный возврат (макс ${maxRefund} ₽)`}
                        type="number"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        placeholder="Полный возврат"
                        error={refundAmountError ?? undefined}
                        data-testid="payment-detail-refund-amount"
                    />

                    <Textarea
                        label={`Причина (минимум ${REASON_MIN_LENGTH} символов)`}
                        value={refundReason}
                        onChange={(e) => setRefundReason(e.target.value)}
                        rows={4}
                        error={refundReasonError ?? undefined}
                        data-testid="payment-detail-refund-reason"
                        autoFocus
                    />

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setRefundOpen(false)}
                            className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
                        >
                            Отмена
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                void submitRefund();
                            }}
                            disabled={isRefunding}
                            data-testid="payment-detail-refund-submit"
                            className="rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:opacity-50"
                        >
                            Оформить возврат
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
